import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Check, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CasesRepository } from "../../repositories/CasesRepository";
import { ConflictReason } from "../../models/ConflictReason";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { plural } from "../../helpers/CommonHelpers";

const RESULTS_SHOWN = 50;
const SEARCH_DELAY_MS = 300;

// The prefix the server cuts off before matching a number listed on its own (RulePlaceholders.WithoutPrefix).
const withoutPrefix = (caseNumber: string) => caseNumber.trim().replace(/^[A-Za-z]+-/, "").toLowerCase();
const isBareEntry = (entry: string) => /^[0-9]/.test(entry.trim());

interface RuleCaseSearchProps {
  /** The entries already in the list, compared ignoring letter case. */
  listed: string[];
  /** The rule's conflict types: only cases holding an unresolved conflict of one of them are offered. */
  reasons: ConflictReason[];
  onAdd: (caseNumbers: string[]) => void;
  onRemove: (caseNumber: string) => void;
  disabled?: boolean;
}

/**
 * "Find cases": pages through the organization's cases that hold an unresolved conflict of the rule's types, by case
 * number, and adds the ones picked to the case list. It does not apply the rule's conditions, so a case whose groups
 * the rule skips stays in the results after a run; paging is how the rest are reached. Picking a listed case takes it
 * out again, unless only a number listed on its own covers it, which the list itself must change.
 */
const RuleCaseSearch: React.FC<RuleCaseSearchProps> = ({ listed, reasons, onAdd, onRemove, disabled }) => {
  const { organizationId } = useParams();
  const repository = new CasesRepository();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const reasonsKey = [...reasons].sort().join(",");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(text.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => setPage(1), [reasonsKey]);

  const results = useQuery({
    queryKey: [QUERY_KEYS.RULE_CASE_SEARCH, organizationId, search, reasonsKey, page],
    queryFn: () =>
      repository.getCases({
        caseNumber: search ? [search] : null,
        conflictReasons: reasons,
        pageNumber: page,
        pageSize: RESULTS_SHOWN,
        orderBy: "CaseNumber",
        descending: false,
      }),
    enabled: open && reasons.length > 0,
    placeholderData: keepPreviousData,
  });

  const listedKeys = new Set(listed.map((entry) => entry.trim().toLowerCase()));
  const bareEntries = new Map(listed.filter(isBareEntry).map((entry) => [entry.trim().toLowerCase(), entry]));
  // The entry that covers a case only through its number on its own, such as "201" for TW-201.
  const coveringBareEntry = (caseNumber: string) =>
    listedKeys.has(caseNumber.toLowerCase()) ? undefined : bareEntries.get(withoutPrefix(caseNumber));
  const isCovered = (caseNumber: string) =>
    listedKeys.has(caseNumber.toLowerCase()) || coveringBareEntry(caseNumber) !== undefined;

  const cases = results.data?.data ?? [];
  const total = results.data?.paging?.totalCount ?? cases.length;
  const first = (page - 1) * RESULTS_SHOWN + 1;
  const last = first + cases.length - 1;
  const notListed = cases.filter((c) => !isCovered(c.caseNumber));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={disabled || reasons.length === 0}
          title={reasons.length === 0 ? "Choose a conflict type first" : undefined}
        >
          <Search className="size-3.5" />
          Find cases
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput value={text} onValueChange={setText} placeholder="Search case numbers…" />
          <CommandList>
            {results.isFetching && cases.length === 0 ? (
              <div className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Searching…
              </div>
            ) : results.isError ? (
              <div className="px-3 py-6 text-sm text-destructive">The cases could not be searched. Try again.</div>
            ) : (
              <>
                <CommandEmpty>No case with an unresolved conflict of these types matches.</CommandEmpty>
                <CommandGroup>
                  {cases.map((c) => {
                    const bareEntry = coveringBareEntry(c.caseNumber);
                    const covered = isCovered(c.caseNumber);
                    return (
                      <CommandItem
                        key={c.id}
                        value={c.id}
                        onSelect={() => {
                          if (bareEntry !== undefined) return;
                          if (covered) onRemove(c.caseNumber);
                          else onAdd([c.caseNumber]);
                        }}
                      >
                        <span className="flex-1 truncate font-mono text-xs" title={c.caseNumber}>
                          {c.caseNumber}
                        </span>
                        {bareEntry !== undefined && (
                          <span className="text-xs text-muted-foreground">listed as {bareEntry}</span>
                        )}
                        {covered && <Check className="size-4 shrink-0" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
          {cases.length > 0 && (
            <div className="flex flex-col gap-2 border-t px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {total > cases.length
                    ? `${first.toLocaleString()}–${last.toLocaleString()} of ${plural(total, "case")}`
                    : plural(total, "case")}
                </span>
                {total > RESULTS_SHOWN && (
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={page === 1 || results.isFetching}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={last >= total || results.isFetching}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={notListed.length === 0}
                onClick={() => onAdd(notListed.map((c) => c.caseNumber))}
              >
                Add all {notListed.length.toLocaleString()} shown
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default RuleCaseSearch;
